import {useState, useEffect, useRef, useCallback} from 'react';
import _ from 'lodash';

const UNSYNCED_STUCK_TIMEOUT_MS = 30000;
const SAVED_DEBOUNCE_MS = 3000;
const ALLOW_DISCONNECT_DELAY_MS = 2000;

/**
 * Consolidates all collaboration connection and sync-status logic.
 *
 * Signals consumed:
 *   - TiptapCollabProvider 'connect' / 'disconnect' / 'synced' / 'unsyncedChanges' events
 *   - Browser 'offline' / 'online' events
 *   - A 30-second stuck-unsynced-changes timer
 *
 * @param {object} provider   – TiptapCollabProvider instance (nullable while loading)
 * @param {string} documentId – current document identifier; resets loading state on change
 * @returns {{ isConnected: boolean, isSaving: boolean, isLoadingDocument: boolean }}
 */
export default function useCollabStatus(provider, documentId) {
  const [isProviderConnected, setIsProviderConnected] = useState(true);
  const [hasUnsyncedChangesStuck, setHasUnsyncedChangesStuck] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(true);

  const allowDisconnectNotificationsRef = useRef(false);
  const unsyncedAlertTimeoutRef = useRef(null);

  // Reset loading state when the document changes.
  useEffect(() => {
    setIsLoadingDocument(true);
    allowDisconnectNotificationsRef.current = false;
  }, [documentId]);

  // Gate disconnect notifications until the document has been visible for a
  // short period so users aren't startled by a flash during initial load.
  useEffect(() => {
    if (!isLoadingDocument) {
      const timer = setTimeout(() => {
        allowDisconnectNotificationsRef.current = true;
      }, ALLOW_DISCONNECT_DELAY_MS);

      return () => clearTimeout(timer);
    }
  }, [isLoadingDocument]);

  // --------------- Provider & browser event listeners ---------------
  useEffect(() => {
    if (!provider) return;

    const handleSynced = () => {
      setIsLoadingDocument(false);
    };

    const handleConnect = () => {
      setIsProviderConnected(true);
    };

    const handleDisconnect = () => {
      if (allowDisconnectNotificationsRef.current) {
        setIsProviderConnected(false);
      }
    };

    const handleOffline = () => {
      if (allowDisconnectNotificationsRef.current) {
        setIsProviderConnected(false);
      }
    };

    const handleOnline = () => {
      setIsProviderConnected(true);
    };

    if (provider.isSynced) {
      setIsLoadingDocument(false);
    }

    provider.on('synced', handleSynced);
    provider.on('connect', handleConnect);
    provider.on('disconnect', handleDisconnect);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      provider.off('synced', handleSynced);
      provider.off('connect', handleConnect);
      provider.off('disconnect', handleDisconnect);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [provider]);

  // --------------- Saving / unsynced-changes logic ---------------
  const debouncedSetIsSavingFalse = useCallback(
    _.debounce(() => {
      setIsSaving(false);
    }, SAVED_DEBOUNCE_MS),
    []
  );

  const handleUnsyncedChanges = useCallback(
    (n) => {
      if (n.number > 0) {
        setIsSaving(true);
        debouncedSetIsSavingFalse.cancel();

        if (!unsyncedAlertTimeoutRef.current) {
          unsyncedAlertTimeoutRef.current = setTimeout(() => {
            setHasUnsyncedChangesStuck(true);
            unsyncedAlertTimeoutRef.current = null;
          }, UNSYNCED_STUCK_TIMEOUT_MS);
        }
      } else {
        debouncedSetIsSavingFalse();

        if (unsyncedAlertTimeoutRef.current) {
          clearTimeout(unsyncedAlertTimeoutRef.current);
          unsyncedAlertTimeoutRef.current = null;
        }

        setHasUnsyncedChangesStuck(false);
      }
    },
    [debouncedSetIsSavingFalse]
  );

  useEffect(() => {
    if (!provider) return;

    provider.on('unsyncedChanges', handleUnsyncedChanges);

    return () => {
      provider.off('unsyncedChanges', handleUnsyncedChanges);
      if (unsyncedAlertTimeoutRef.current) {
        clearTimeout(unsyncedAlertTimeoutRef.current);
      }
    };
  }, [provider, handleUnsyncedChanges]);

  const isConnected = isProviderConnected && !hasUnsyncedChangesStuck;

  return {isConnected, isSaving, isLoadingDocument};
}
